import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, MapPin, Copy, Check, Shield, UserPlus, Send, MessageSquare, Volume2, Info } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useEmergencyStore } from '../../store/useEmergencyStore';
import styles from './EmergencyPage.module.css';

export default function EmergencyPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.substring(0, 2);
  const [address, setAddress] = useState(t('emergency.findingLocation'));
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { contact, setContact, clearContact } = useEmergencyStore();
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [tempContact, setTempContact] = useState({ name: '', phone: '', message: '' });

  // 컴포넌트 마운트 시 즉시 위치 가져오기
  useEffect(() => {
    getMyAddress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaveContact() {
    setContact(tempContact);
    setIsEditingContact(false);
  }

  function handleSpeak() {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance("저는 외국인입니다. 도움이 필요합니다. 경찰이나 소방서에 연락해 주세요.");
      utterance.lang = 'ko-KR';
      window.speechSynthesis.speak(utterance);
    } else {
      alert("Speech synthesis is not supported in this browser.");
    }
  }

  function handleSendSos() {
    if (!contact.phone) return;
    const body = encodeURIComponent(`${contact.message}\n\n내 위치: ${address}`);
    window.location.href = `sms:${contact.phone}?body=${body}`;
  }

  async function handleImportContact() {
    if (Capacitor.isNativePlatform()) {
      try {
        const { Contacts } = await import('@capacitor-community/contacts');
        const result = await Contacts.pickContact({
          projection: { name: true, phones: true }
        });
        if (result && result.contact) {
          const name = result.contact.name?.display || '';
          const phoneObj = result.contact.phones?.[0];
          const phone = phoneObj ? phoneObj.number : '';
          setTempContact(prev => ({ ...prev, name, phone }));
        }
      } catch (error) {
        console.log('Failed to pick contact', error);
      }
      return;
    }

    if ('contacts' in navigator) {
      try {
        const props = ['name', 'tel'];
        const opts = { multiple: false };
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          const name = contacts[0].name?.[0] || '';
          const phone = contacts[0].tel?.[0] || '';
          setTempContact(prev => ({ ...prev, name, phone }));
        }
      } catch (error) {
        console.log('Web Contacts API failed', error);
      }
      return;
    }

    alert('스마트폰 주소록 연동은 네이티브 앱이거나 지원되는 모바일 브라우저에서만 가능합니다.');
  }

  function getMyAddress() {
    setLoading(true);
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        // 카카오 역지오코딩
        if (window.kakao?.maps?.services) {
          const geocoder = new window.kakao.maps.services.Geocoder();
          geocoder.coord2Address(coords.longitude, coords.latitude, (result, status) => {
            if (status === window.kakao.maps.services.Status.OK) {
              const addr = result[0]?.road_address?.address_name
                || result[0]?.address?.address_name
                || t('emergency.addressNotFound');
              setAddress(addr);
              localStorage.setItem('ansim_manual_address', addr);
            }
            setLoading(false);
          });
        } else {
          setAddress(`${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`);
          setLoading(false);
        }
      },
      (error) => {
        if (error.code === 1) {
          const manualAddr = localStorage.getItem('ansim_manual_address');
          if (manualAddr) {
            setAddress(manualAddr + ' (수동입력)');
          } else {
            setAddress(t('emergency.needPermission'));
          }
        } else {
          setAddress(t('emergency.needPermission'));
        }
        setLoading(false);
      },
      { timeout: 10000 }
    );
  }

  function copyAddress() {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Shield size={28} color="white" />
        <h1>{t('emergency.title')}</h1>
        <p>{t('emergency.description')}</p>
      </div>

      <div className={styles.content}>
        {/* 도움 요청 플래시 카드 */}
        <div className={styles.flashcardCard}>
          <div className={styles.flashcardTitle}>{t('emergency.flashcardTitle')}</div>
          <div className={styles.flashcardMessage}>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: currentLang !== 'ko' ? '8px' : '0' }}>
              저는 외국인입니다. 도움이 필요합니다. 경찰(112)이나 소방서(119)에 연락해 주세요.
            </div>
            {currentLang !== 'ko' && (
              <div style={{ fontSize: '0.95rem', color: 'var(--color-text-muted)' }}>
                {t('emergency.flashcardMessage')}
              </div>
            )}
          </div>
          <button className={styles.btnSpeak} onClick={handleSpeak}>
            <Volume2 size={20} />
            {t('emergency.flashcardSpeak')}
          </button>
        </div>

        {/* 긴급전화 버튼 */}
        <div className={styles.emergencyButtons}>
          <a href="tel:112" id="btn-call-112" className={styles.emergencyBtn}>
            <div className={styles.emergencyIcon} style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' }}>
              <Phone size={24} color="white" strokeWidth={2.5} />
            </div>
            <div className={styles.emergencyInfo}>
              <span className={styles.emergencyNumber} style={{ fontSize: '1.5rem' }}>112</span>
              <span className={styles.emergencyLabel}>{t('emergency.policeLabel')}</span>
            </div>
          </a>

          <a href="tel:119" id="btn-call-119" className={styles.emergencyBtn}>
            <div className={styles.emergencyIcon} style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)' }}>
              <Phone size={24} color="white" strokeWidth={2.5} />
            </div>
            <div className={styles.emergencyInfo}>
              <span className={styles.emergencyNumber} style={{ fontSize: '1.5rem' }}>119</span>
              <span className={styles.emergencyLabel}>{t('emergency.fireLabel')}</span>
            </div>
          </a>

          <a href="tel:1330" id="btn-call-1330" className={styles.emergencyBtn}>
            <div className={styles.emergencyIcon} style={{ background: 'linear-gradient(135deg, #047857, #10b981)' }}>
              <Info size={28} color="white" strokeWidth={2.5} />
            </div>
            <div className={styles.emergencyInfo}>
              <span className={styles.emergencyNumber} style={{ fontSize: '1.5rem' }}>1330</span>
              <span className={styles.emergencyLabel}>{t('emergency.guideTourist1330')}</span>
            </div>
          </a>
        </div>

        {/* 내 위치 카드 */}
        <div className={`card ${styles.locationCard}`}>
          <div className={styles.locationHeader}>
            <MapPin size={18} color="var(--color-sky)" />
            <h3>{t('emergency.myAddress')}</h3>
          </div>

          <div className={styles.addressBox}>
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="spinner" style={{ width: 16, height: 16 }} />
                <span className="text-muted text-sm">{t('emergency.gettingLocation')}</span>
              </div>
            ) : (
              <p className={styles.addressText}>{address}</p>
            )}
          </div>

          <div className={styles.locationActions}>
            <button
              id="btn-get-location"
              className="btn btn-primary"
              onClick={getMyAddress}
              disabled={loading}
              style={{ flex: 1 }}
            >
              <MapPin size={16} />
              {t('emergency.getCurrentLocation')}
            </button>
            <button
              id="btn-copy-address"
              className="btn btn-ghost"
              onClick={copyAddress}
              style={{ flex: 1, border: '1px solid var(--color-border)' }}
            >
              {copied ? <Check size={16} color="var(--color-success)" /> : <Copy size={16} />}
              {copied ? t('emergency.copied') : t('emergency.copyAddress')}
            </button>
          </div>

          <p className={styles.locationTip}>
            {t('emergency.foreignerTip')}
            <br />
            <span className={styles.addressEn}>{t('emergency.foreignerTipEn')}</span>
          </p>
        </div>

        {/* SOS Contact Card */}
        <div className={styles.sosCard}>
          <div className={styles.sosHeader}>
            <div className={styles.sosHeaderTitle}>
              <UserPlus size={18} color="var(--color-navy)" />
              <span>{t('emergency.sosTitle')}</span>
            </div>
            {contact.phone && !isEditingContact && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-ghost" 
                  style={{ padding: '4px 8px', fontSize: '0.8rem', height: 'auto', color: 'var(--color-danger, #dc2626)' }}
                  onClick={() => {
                    if (window.confirm(t('emergency.confirmDelete'))) {
                      clearContact();
                    }
                  }}
                >
                  {t('emergency.delete')}
                </button>
                <button 
                  className="btn btn-ghost" 
                  style={{ padding: '4px 8px', fontSize: '0.8rem', height: 'auto' }}
                  onClick={() => {
                    setTempContact(contact);
                    setIsEditingContact(true);
                  }}
                >
                  {t('emergency.edit')}
                </button>
              </div>
            )}
          </div>

          {(!contact.phone || isEditingContact) ? (
            <div className={styles.sosForm}>
              <button 
                className={`btn btn-ghost`} 
                onClick={handleImportContact}
                style={{ width: '100%', marginBottom: '16px', border: '1px dashed var(--color-sky)', color: 'var(--color-navy)', backgroundColor: 'var(--color-primary-50)' }}
              >
                <UserPlus size={16} style={{ marginRight: '8px' }} color="var(--color-navy)" /> 
                {t('importContacts')}
              </button>
              <div className={styles.formGroup}>
                <label>{t('emergency.nameRelation')}</label>
                <input 
                  type="text" 
                  className={styles.input} 
                  placeholder={t('emergency.namePlaceholder')}
                  value={tempContact.name}
                  onChange={(e) => setTempContact({ ...tempContact, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('emergency.phone')}</label>
                <input 
                  type="tel" 
                  className={styles.input} 
                  placeholder={t('emergency.phonePlaceholder')}
                  value={tempContact.phone}
                  onChange={(e) => setTempContact({ ...tempContact, phone: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('emergency.presetMessage')}</label>
                <textarea 
                  className={styles.textarea}
                  value={tempContact.message}
                  onChange={(e) => setTempContact({ ...tempContact, message: e.target.value })}
                />
              </div>
              <div className={styles.sosActions}>
                {contact.phone && (
                  <button 
                    className="btn btn-ghost" 
                    style={{ flex: 1 }}
                    onClick={() => setIsEditingContact(false)}
                  >
                    {t('emergency.cancel')}
                  </button>
                )}
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 2 }}
                  onClick={handleSaveContact}
                  disabled={!tempContact.phone || !tempContact.message}
                >
                  {t('emergency.save')}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.sosDisplay}>
              <div className={styles.sosContactInfo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MessageSquare size={16} color="var(--color-text-secondary)" />
                  <span className={styles.sosName}>{contact.name || t('emergency.defaultContact')}</span>
                  <span className={styles.sosPhone}>{contact.phone}</span>
                </div>
                <div className={styles.sosMessagePreview}>
                  "{contact.message}"<br/>
                  {t('emergency.plusCurrentAddress')}
                </div>
              </div>
              <button 
                className={`btn ${styles.btnSendSos}`}
                style={{ width: '100%' }}
                onClick={handleSendSos}
              >
                <Send size={18} />
                {t('emergency.sendSms')}
              </button>
            </div>
          )}
        </div>

        {/* 안내 */}
        <div className={styles.guideCard}>
          <h3 className={styles.guideTitle}>{t('emergency.guideTitle')}</h3>
          <ul className={styles.guideList}>
            <li><strong>112</strong> — {t('emergency.guidePolice')}</li>
            <li><strong>119</strong> — {t('emergency.guideFire')}</li>
            <li><strong>120</strong> — {t('emergency.guideCivil')}</li>
            <li><strong>1330</strong> — {t('emergency.guideTourist')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
